import { type ReactNode } from 'react';

import { cn } from '@/lib/utils';

type Tone = 'paper' | 'secondary' | 'dark';

const toneClasses: Record<Tone, string> = {
  paper: 'bg-paper text-ink',
  secondary: 'bg-secondary text-ink',
  dark: 'bg-dark-ground text-dark-ground-ink',
};

/**
 * Eyebrow — mono uppercase kicker above a section heading.
 * The one label style every section shares.
 */
export function Eyebrow({
  children,
  className,
  tone = 'paper',
}: {
  children: ReactNode;
  className?: string;
  tone?: Tone;
}) {
  return (
    <span
      className={cn(
        'font-mono text-eyebrow font-semibold uppercase tracking-[0.18em]',
        // On dark grounds the tiny label needs a lifted indigo to stay legible;
        // on light it keeps the deep brand.
        tone === 'dark' ? 'text-brand-lift' : 'text-brand dark:text-brand-lift',
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * SectionShell — the standard section wrapper: consistent container width,
 * vertical rhythm, and an optional centered eyebrow/title/subtitle header.
 * Bespoke sections (hero, tracking) lay themselves out; everything else
 * composes this so the page reads as one system.
 */
export function SectionShell({
  id,
  eyebrow,
  title,
  subtitle,
  tone = 'paper',
  align = 'center',
  className,
  headerClassName,
  children,
}: {
  id?: string;
  eyebrow?: ReactNode;
  title?: ReactNode;
  subtitle?: ReactNode;
  tone?: Tone;
  align?: 'center' | 'left';
  className?: string;
  headerClassName?: string;
  children?: ReactNode;
}) {
  const hasHeader = eyebrow || title || subtitle;

  return (
    <section
      id={id}
      className={cn(
        'w-full py-[var(--section-y-sm)] md:py-[var(--section-y)]',
        toneClasses[tone],
        className,
      )}
    >
      <div className="mx-auto w-full max-w-[var(--container)] px-6">
        {hasHeader && (
          <div
            className={cn(
              'flex flex-col gap-5',
              align === 'center' ? 'items-center text-center' : 'items-start text-left',
              headerClassName,
            )}
          >
            {eyebrow && <Eyebrow tone={tone}>{eyebrow}</Eyebrow>}
            {title && (
              <h2 className="max-w-3xl font-display text-h2 font-bold tracking-[-0.02em]">
                {title}
              </h2>
            )}
            {subtitle && (
              <p
                className={cn(
                  'max-w-2xl text-lead',
                  tone === 'dark' ? 'text-dark-ground-soft' : 'text-ink-soft',
                )}
              >
                {subtitle}
              </p>
            )}
          </div>
        )}
        {children}
      </div>
    </section>
  );
}
